import { resetSelectedOptions } from './utils';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import {
  createQueryParams,
  getTableQueryParams,
  readQueryParams
} from '@cp/src/utils/QueryParamsHelper';
import { FilterIconFilled } from '@navi/web-ui/lib/icons';
import { Button, Drawer, Typography } from '@navi/web-ui/lib/primitives';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tooltip as TooltipV2,
  Directions,
  TooltipContent,
  TooltipTrigger
} from '../TooltipV2/TooltipV2';
import styles from './index.module.scss';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import SelectedFilters from './SelectedFilters';
import { IFilterDrawer, ISelectedOptions } from './types';
import { FiltersWithSubfiltersMapping } from './constants';
import { DEFAULT_QUERY_PARAM_TABLE_IDENTIFIER } from './constants';
import RecommendedListBanner from '@cp/src/pages/CaseRecommendation/components/RecommendedListBanner';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import FilterButton from './FilterButton';

const FilterDrawer = ({
  filterButtonTitle,
  filterSchema,
  drawerContainerClass,
  leftContainerClass,
  rightContainerClass,
  selectedFiltersClass,
  onApply,
  tableName,
  queryParamTableIdentifier = DEFAULT_QUERY_PARAM_TABLE_IDENTIFIER,
  filterKeys,
  defaultParams,
  renderSelectedFiltersExternally = false,
  toShowCustomBanner = false,
  onOpenFiltersDrawer,
  selectedFiltersWrapperContainer,
  disabled = false,
  isOldFlow = true
}: IFilterDrawer) => {
  const mainParams = readQueryParams();
  const queryParams = getTableQueryParams(filterSchema, queryParamTableIdentifier, true);
  const navigate = useNavigate();

  const filterSchemaMap = useMemo(() => {
    const keys = Object.keys(filterSchema);
    filterKeys && keys.push(...filterKeys); //Adding keys for subfilters
    return keys;
  }, [filterSchema, filterKeys]);

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<ISelectedOptions>({});

  useEffect(() => {
    if (filterSchemaMap?.length && Object.keys(selectedOptions)?.length && !selectedFilter) {
      setSelectedFilter(filterSchemaMap?.[0]);
    }
  }, [filterSchemaMap, selectedOptions]);

  useEffect(() => {
    resetSelectedOptions({ filterSchemaMap, queryParams, setSelectedOptions });
  }, [showFilterDrawer]);

  const handleLeftPanelFilterChange = (filterKey: string) => {
    addClickstreamEvent(
      {
        name: `LH_${tableName}_FILTER_CHANGE`,
        description: 'Filter change'
      },
      {
        filterKey,
        selectedOptions
      }
    );
    setSelectedFilter(filterKey);
  };

  const handleDateChangesInParams = (selectedFilters: Record<string, string[]>) => {
    if (
      !selectedFilters?.teleInteractionStatuses?.includes(
        FiltersWithSubfiltersMapping.PROMISE_TO_PAY
      )
    ) {
      selectedFilters.promiseToPayDateTeleCsa = [];
    }
    if (
      !selectedFilters?.teleInteractionStatuses?.includes(
        FiltersWithSubfiltersMapping.REQUESTED_CALLBACK
      )
    ) {
      selectedFilters.requestedCallbackDateTeleCsa = [];
    }
    if (
      !selectedFilters?.fieldInteractionStatuses?.includes(
        FiltersWithSubfiltersMapping.PROMISE_TO_PAY
      )
    ) {
      selectedFilters.promiseToPayDateFieldAgent = [];
    }
  };

  const flattenNestedFilters = (
    selectedFilters: Record<string, string[] | Record<string, string[]>>
  ) => {
    Object.keys(selectedFilters).forEach(filterKey => {
      if (filterSchema[filterKey]?.nestedFilter) {
        const nestedFilters = selectedFilters[filterKey] as Record<string, string[]>;
        Object.keys(nestedFilters).forEach(nestedFilterKey => {
          selectedFilters[`${filterKey}_${nestedFilterKey}`] = nestedFilters[nestedFilterKey];
        });
        delete selectedFilters[filterKey];
      }
    });
  };

  const applyFilterChangeHandler = (selectedFilters: Record<string, string[]>) => {
    handleDateChangesInParams(selectedFilters);
    flattenNestedFilters(selectedFilters);
    const url = createQueryParams({
      ...mainParams,
      [queryParamTableIdentifier]: {
        ...mainParams[queryParamTableIdentifier],
        ...defaultParams,
        ...selectedFilters
      }
    });
    navigate(url);
    onApply?.(selectedFilters);
  };

  const clearFilterWithFilterKey = useCallback(
    (filterKey: string) => {
      const eventName = `LH_${tableName}_FILTER_REMOVE_BUTTON_CLICKED`;
      addClickstreamEvent(
        {
          name: eventName,
          description: eventName
        },
        { filterKey }
      );
      applyFilterChangeHandler(
        resetSelectedOptions({
          deleteFilterKey: filterKey,
          filterSchemaMap,
          queryParams,
          setSelectedOptions
        })
      );
    },
    [queryParams, filterSchemaMap]
  );

  const filterCtaHandler = useCallback(
    (reset?: boolean) => {
      const eventName = `LH_${tableName}_FILTER_${reset ? 'CLEAR_ALL' : 'APPLY'}_BUTTON_CLICKED`;
      if (reset) {
        const updatedFilters = resetSelectedOptions({
          clearAll: true,
          filterSchemaMap,
          queryParams,
          setSelectedOptions
        });
        addClickstreamEvent(
          {
            name: eventName,
            description: eventName
          },
          {
            prevSelectedOptions: selectedOptions
          }
        );
        applyFilterChangeHandler(updatedFilters);
        setSelectedFilter(filterSchemaMap?.[0]);
      } else {
        addClickstreamEvent(
          {
            name: eventName,
            description: eventName
          },
          { selectedOptions }
        );
        applyFilterChangeHandler(selectedOptions);
      }
      setShowFilterDrawer(false);
    },
    [filterSchemaMap, queryParams, selectedOptions]
  );

  useEffect(() => {
    if (onOpenFiltersDrawer) {
      const handleOpenDrawer = () => setShowFilterDrawer(true);
      onOpenFiltersDrawer(handleOpenDrawer);
    }
  }, [onOpenFiltersDrawer]);

  const isFilterButtonDisabled = !filterSchemaMap?.length || disabled;

  return (
    <>
      {isOldFlow ? (
        <div className={cx(styles.tableFilters, selectedFiltersClass)}>
          {!renderSelectedFiltersExternally && (
            <SelectedFilters
              filterSchemaMap={filterSchemaMap}
              filterSchema={filterSchema}
              handleFilterChange={clearFilterWithFilterKey}
              clearAllFilters={filterCtaHandler}
              queryParamTableIdentifier={queryParamTableIdentifier}
              onMoreFiltersClick={() => setShowFilterDrawer(true)}
            />
          )}
          <Button
            startAdornment={<FilterIconFilled color="var(--blue-base)" />}
            variant="secondary"
            onClick={() => {
              setShowFilterDrawer(true);
              const eventName = `LH_${tableName}_FILTER_BUTTON_CLICKED`;
              addClickstreamEvent({
                name: eventName,
                description: eventName
              });
            }}
            disabled={!filterSchemaMap?.length}
          >
            {!filterSchemaMap?.length ? (
              <TooltipV2 placement={Directions.TOP} hideStrategy="referenceHidden">
                <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
                  <Typography variant="h4">{filterButtonTitle}</Typography>
                </TooltipTrigger>
                <TooltipContent className={'tooltipWrapper'}>No filters available</TooltipContent>
              </TooltipV2>
            ) : (
              filterButtonTitle
            )}
          </Button>
        </div>
      ) : (
        <div className={cx(styles.tableFilters, selectedFiltersClass)}>
          <FilterButton
            filterSchemaMap={filterSchemaMap}
            filterSchema={filterSchema}
            clearAllFilters={filterCtaHandler}
            queryParamTableIdentifier={queryParamTableIdentifier}
            selectedFiltersWrapperContainer={selectedFiltersWrapperContainer}
            openFilterDrawer={() => {
              setShowFilterDrawer(true);
              const eventName = `LH_${tableName}_FILTER_BUTTON_CLICKED`;
              addClickstreamEvent({
                name: eventName,
                description: eventName
              });
            }}
            isFilterButtonDisabled={isFilterButtonDisabled}
          />
        </div>
      )}
      <Drawer
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        headerText="FILTER BY"
        showHeaderDivider={true}
        usePadding={false}
        className={cx(styles.filterDrawer, drawerContainerClass)}
      >
        {toShowCustomBanner && (
          <div className="px-4 py-2 border-0 border-b-[1px] border-solid border-[var(--greyscale-light)] ">
            <RecommendedListBanner
              primaryText="View Recommended Case List"
              secondaryText="Click here to hit your Collection targets"
              imageHeight={48}
              imageWidth={48}
              onBeforeModalOpen={() => {
                setShowFilterDrawer(false);
              }}
              clickstreamEventName={
                CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_FILTER_DRAWER_BANNER_CLICK.name
              }
              clickstreamEventDescription={
                CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_FILTER_DRAWER_BANNER_CLICK
                  .description
              }
            />
          </div>
        )}
        {showFilterDrawer && (
          <div className={cx(styles.container, { [styles.withBanner]: toShowCustomBanner })}>
            <section className={styles.tabSection}>
              <section className={cx(styles.filterContainer, leftContainerClass)}>
                <LeftPanel
                  filterSchemaMap={filterSchemaMap}
                  filterSchema={filterSchema}
                  selectedFilter={selectedFilter}
                  selectedOptions={selectedOptions}
                  handleFilterChange={handleLeftPanelFilterChange}
                />
              </section>
              <section className={cx(styles.optionsContainer, rightContainerClass)}>
                <RightPanel
                  key={selectedFilter}
                  filterSchema={filterSchema}
                  setSelectedOptions={setSelectedOptions}
                  selectedFilter={selectedFilter}
                  selectedOptions={selectedOptions}
                  tableName={tableName}
                />
              </section>
            </section>

            <section className={styles.cta}>
              <Button
                variant="secondary"
                className={styles.button}
                onClick={() => filterCtaHandler(true)}
              >
                Clear All
              </Button>
              <Button className={styles.button} onClick={() => filterCtaHandler(false)}>
                Apply
              </Button>
            </section>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default FilterDrawer;
